import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Layout/Header/Header';
import SEO from '../components/SEO';
import ErrorBoundary from '../components/Common/ErrorBoundary/ErrorBoundary';
import ErrorFallback from '../components/Common/ErrorFallback/ErrorFallback';
import { useContactModal } from '../stores/modalStore';
import { useScrollState } from '../stores/scrollStore';
import { useLoadingScreen } from '../stores/modalStore';

// Lazy load heavy components
const Hero = lazy(() => import('../components/Home/Hero/Hero'));
const Services = lazy(() => import('../components/Home/Services/Services'));
const Process = lazy(() => import('../components/Home/Process/Process'));
const Impact = lazy(() => import('../components/Home/Impact/Impact'));
const Trust = lazy(() => import('../components/Home/Trust/Trust'));
const Testimonials = lazy(() => import('../components/Home/Testimonials/Testimonials'));
const CTA = lazy(() => import('../components/Home/CTA/CTA'));
const Contact = lazy(() => import('../components/Home/Contact/Contact'));
const Globe = lazy(() => import('../components/Home/Globe/Globe'));
const Footer = lazy(() => import('../components/Layout/Footer/Footer'));
const PlatformShowcase = lazy(() => import('../components/Home/PlatformShowcase/PlatformShowcase'));
const ContactModal = lazy(() => import('../components/Layout/ContactModal/ContactModal'));

const Home: React.FC = () => {
  const { isScrolled, scrollProgress, throttledUpdateScrollState } = useScrollState();
  const { isOpen: isContactModalOpen, close: closeContactModal } = useContactModal();
  const { setVisible: setShowLoading } = useLoadingScreen();

  // Handle Hero loading completion
  const handleHeroLoaded = () => {
    // Hide loading screen when Hero is fully loaded
    setShowLoading(false);
  };
  
  // Component visibility flags - set to false to hide components
  const componentFlags = {
    hero: true,
    services: true,
    platformShowcase: true,
    process: true,
    impact: true,
    trust: true,
    testimonials: false, // Disabled - no testimonials yet
    cta: true,
    contact: true,
    globe: true,
    footer: true,
  };
  
  useEffect(() => {
    // Use throttled scroll handler instead of polling
    const handleScroll = () => {
      throttledUpdateScrollState(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [throttledUpdateScrollState]);
  return (
    <>
      <SEO 
        title="Cinquity | AI-Driven eCOA Platform for Clinical Trials"
        description="Transform clinical trials with Cinquity's AI-powered eCOA platform. Accelerate trial operations with intelligent assessment creation, localization, and data insights. Trusted by leading pharmaceutical companies worldwide."
        keywords="eCOA, clinical trials, AI, electronic clinical outcome assessments, pharmaceutical, clinical data, trial management, Cinquity, healthcare technology, clinical trial software"
      />
      <Header isScrolled={isScrolled} scrollProgress={scrollProgress} />
      <div>
        <div>
          {componentFlags.hero && (
            <ErrorBoundary fallback={<ErrorFallback type="component" />}>
              <Suspense fallback={<div className="component-loader">Loading Hero...</div>}>
                <Hero onHeroLoaded={handleHeroLoaded} />
              </Suspense>
            </ErrorBoundary>
          )}
          {componentFlags.platformShowcase && (
            <Suspense fallback={<div className="component-loader">Loading Platform...</div>}>
              <PlatformShowcase />
            </Suspense>
          )}
          {componentFlags.services && (
            <Suspense fallback={<div className="component-loader">Loading Services...</div>}>
              <Services />
            </Suspense>
          )}
          {componentFlags.process && (
            <Suspense fallback={<div className="component-loader">Loading Process...</div>}>
              <Process />
            </Suspense>
          )}
          {componentFlags.impact && (
            <Suspense fallback={<div className="component-loader">Loading Impact...</div>}>
              <Impact />
            </Suspense>
          )}
          {componentFlags.trust && (
            <Suspense fallback={<div className="component-loader">Loading Trust...</div>}>
              <Trust />
            </Suspense>
          )}
          {componentFlags.testimonials && (
            <Suspense fallback={<div className="component-loader">Loading Testimonials...</div>}>
              <Testimonials />
            </Suspense>
          )}
          {componentFlags.cta && (
            <ErrorBoundary fallback={<ErrorFallback type="component" />}>
              <Suspense fallback={<div className="component-loader">Loading CTA...</div>}>
                <CTA />
              </Suspense>
            </ErrorBoundary>
          )}
          {componentFlags.contact && (
            <Suspense fallback={<div className="component-loader">Loading Contact...</div>}>
              <Contact />
            </Suspense>
          )}
          {componentFlags.globe && (
            <ErrorBoundary fallback={<ErrorFallback type="component" />}>
              <Suspense fallback={<div className="component-loader">Loading Globe...</div>}>
                <Globe />
              </Suspense>
            </ErrorBoundary>
          )}
          {componentFlags.footer && (
            <Suspense fallback={<div className="component-loader">Loading Footer...</div>}>
              <Footer />
            </Suspense>
          )}
        </div>

        <Suspense fallback={<div className="component-loader">Loading Modal...</div>}>
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={closeContactModal}
          />
        </Suspense>
      </div>
    </>
  );
};

export default Home;
